/**
 * The [min, max] a CUSTOM_SCALAR-shaded point cloud ramps its colours over.
 *
 * Two analyses write a per-point scalar into their derived cloud and ask the
 * viewer to shade by it:
 *
 * - **Deviation** — `metadata.scalarAttribute = 'Deviation'` plus
 *   `metadata.deviationRange`.
 * - **Clearance** — `metadata.scalarAttribute = 'Clearance'` plus
 *   `metadata.clearanceRange` (`[-rampSpanM, +rampSpanM]`, written by the
 *   clearance postprocess; ruling L4/R8 names it the ONE source of truth for
 *   how a clearance cloud is coloured).
 *
 * Before this helper existed the renderer read `deviationRange` unconditionally,
 * so a clearance cloud — which carries no `deviationRange` — fell back to
 * deviation's ±0.05 m default and shaded a metres-wide clearance run over a
 * 5 cm ramp, hiding the intrusion depth the analysis exists to measure.
 *
 * Parity note: for well-formed deviation metadata the result is exactly what
 * the old `metadata.deviationRange ?? [-0.05, 0.05]` produced. The pair guard
 * only changes what happens for a malformed range (a non-array, a wrong-length
 * array, `NaN`s), which previously reached the shader as `undefined`/`NaN`.
 */

/** Deviation's historical default ramp: ±5 cm. */
export const DEFAULT_SCALAR_RANGE: readonly [number, number] = [-0.05, 0.05];

/** The layer-metadata fields this resolution looks at. `Layer.metadata` is an
 *  untyped JSON scalar, so every field arrives as `unknown` and is guarded. */
export interface ScalarRangeMetadata {
  scalarAttribute?: unknown;
  deviationRange?: unknown;
  clearanceRange?: unknown;
}

/** A user override from `LayerSettings` — either end may be independently set. */
export interface ScalarRangeOverride {
  scalarRangeMin?: number | null;
  scalarRangeMax?: number | null;
}

const isRangePair = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  typeof value[0] === 'number' &&
  typeof value[1] === 'number' &&
  Number.isFinite(value[0]) &&
  Number.isFinite(value[1]);

/**
 * Resolve the scalar range a layer's metadata asks for, keyed on which scalar
 * the cloud is actually shading by. Unknown/absent attributes keep deviation's
 * behaviour (that is the historical default, and `customAttributeName` falls
 * back to `'Deviation'` the same way).
 */
export const resolveScalarRange = (metadata: ScalarRangeMetadata | null | undefined): [number, number] => {
  const candidate = metadata?.scalarAttribute === 'Clearance' ? metadata?.clearanceRange : metadata?.deviationRange;
  return isRangePair(candidate) ? [candidate[0], candidate[1]] : [DEFAULT_SCALAR_RANGE[0], DEFAULT_SCALAR_RANGE[1]];
};

/**
 * The same resolution with the per-layer manual override applied on top —
 * either end independently, exactly as the renderer and the colorbar do.
 */
export const resolveScalarRangeWithOverride = (
  metadata: ScalarRangeMetadata | null | undefined,
  override?: ScalarRangeOverride | null,
): [number, number] => {
  const [min, max] = resolveScalarRange(metadata);
  return [override?.scalarRangeMin ?? min, override?.scalarRangeMax ?? max];
};
